/**
 * @fileoverview Filter Type API Module
 * This module provides API functions for filtering and fetching different types of entities
 * in both outreach and manual task contexts. It supports filtering by:
 * - Members: Individual member records
 * - Providers: Healthcare provider information
 * - Pharmacies: Pharmacy partner data
 * - Programs: Healthcare program details
 *
 * Each filter type supports pagination, search functionality, and optional grouping.
 * The module includes separate functions for outreach filters and manual task filters.
 */

import api from "./api";
export const fetchMember = async (params = {}) => {
  const updatedParams = {
    outreachType: params.outreachType || "",
    filterType: "MEMBER",
    page: params.page || 0,
    size: params.size || 30,
    ...params,
  };

  const apiParams = {
    ...updatedParams,
  };

  const response = await api.get(`/outreach/filter`, {
    params: apiParams,
    headers: {
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
    },
  });

  return response.data.content;
};

export const fetchProviders = async (params = {}) => {
  const updatedParams = {
    outreachType: params.outreachType || "",
    filterType: "PROVIDER",
    page: params.page || 0,
    size: params.size || 30,
    ...params,
  };

  const apiParams = { ...updatedParams };
  if ("groupBy" in params) {
    apiParams.groupBy = params.groupBy;
  }

  const response = await api.get(`/outreach/filter`, {
    params: apiParams,
    headers: {
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
    },
  });

  return response.data.content;
};

export const fetchPharmacies = async (params = {}) => {
  const updatedParams = {
    outreachType: params.outreachType || "",
    filterType: "PHARMACY",
    page: params.page || 0,
    size: params.size || 30,
    ...params,
  };

  const apiParams = { ...updatedParams };
  if ("groupBy" in params) {
    apiParams.groupBy = params.groupBy;
  }

  const response = await api.get(`/outreach/filter`, {
    params: apiParams,
    headers: {
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
    },
  });

  return response.data.content;
};

export const fetchPrograms = async (params = {}) => {
  const updatedParams = {
    outreachType: params.outreachType || "",
    filterType: "PROGRAM",
    ...params,
  };

  const apiParams = { ...updatedParams };
  if ("groupBy" in params) {
    apiParams.groupBy = params.groupBy;
  }

  const response = await api.get(`/outreach/filter`, {
    params: apiParams,
    headers: {
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
    },
  });

  return response.data.content;
};

export const fetchCarriers = async (params = {}) => {
  const updatedParams = {
    outreachType: params.outreachType || "",
    filterType: "CARRIER",
    ...params,
  };

  const apiParams = { ...updatedParams };
  if ("groupBy" in params) {
    apiParams.groupBy = params.groupBy;
  }

  const response = await api.get(`/outreach/filter`, {
    params: apiParams,
    headers: {
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
    },
  });

  return response.data.content;
};
export const fetchGroups = async (params = {}) => {
  const updatedParams = {
    outreachType: params.outreachType || "",
    filterType: "GROUP",
    ...params,
  };

  const apiParams = { ...updatedParams };
  if ("groupBy" in params) {
    apiParams.groupBy = params.groupBy;
  }

  const response = await api.get(`/outreach/filter`, {
    params: apiParams,
    headers: {
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
    },
  });

  return response.data.content;
};

export const fetchAssignees = async (params = {}) => {
  const updatedParams = {
    outreachType: params.outreachType || "",
    filterType: "ASSIGNEE",
    ...params,
  };

  const apiParams = { ...updatedParams };
  if ("groupBy" in params) {
    apiParams.groupBy = params.groupBy;
  }

  const response = await api.get(`/outreach/filter`, {
    params: apiParams,
    headers: {
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
    },
  });

  return response.data.content;
};
export const fetchManualMember = async (params = {}) => {
  try {
    const updatedParams = {
      filterType: "MEMBER",
      page: params.page || 0,
      size: params.size || 30,
      ...params,
    };

    const apiParams = {
      ...updatedParams,
    };

    const response = await api.get(`/api/manual-tasks/filter`, {
      params: apiParams,
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
      },
    });

    if (response.data && Array.isArray(response.data.content)) {
      return response.data.content;
    } else if (response.data && Array.isArray(response.data)) {
      return response.data;
    } else {
      return [];
    }
  } catch (error) {
    return `fetchPriorityOutreach failed: ${error.message}`;
  }
};

export const fetchManualProviders = async (params = {}) => {
  try {
    const updatedParams = {
      filterType: "PROVIDER",
      page: params.page || 0,
      size: params.size || 30,
      ...params,
    };

    const apiParams = { ...updatedParams };
    if ("groupBy" in params) {
      apiParams.groupBy = params.groupBy;
    }

    const response = await api.get(`/api/manual-tasks/filter`, {
      params: apiParams,
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
      },
    });

    if (response.data && Array.isArray(response.data.content)) {
      return response.data.content;
    } else if (response.data && Array.isArray(response.data)) {
      return response.data;
    } else {
      return [];
    }
  } catch (error) {
    return `fetchPriorityOutreach failed: ${error.message}`;
  }
};

export const fetchManualPharmacies = async (params = {}) => {
  try {
    const updatedParams = {
      filterType: "PHARMACY",
      page: params.page || 0,
      size: params.size || 30,
      ...params,
    };

    const apiParams = { ...updatedParams };
    if ("groupBy" in params) {
      apiParams.groupBy = params.groupBy;
    }

    const response = await api.get(`/api/manual-tasks/filter`, {
      params: apiParams,
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
      },
    });

    if (response.data && Array.isArray(response.data.content)) {
      return response.data.content;
    } else if (response.data && Array.isArray(response.data)) {
      return response.data;
    } else {
      return [];
    }
  } catch (error) {
    return `fetchPriorityOutreach failed: ${error.message}`;
  }
};

export const fetchManualPrograms = async (params = {}) => {
  try {
    const updatedParams = {
      filterType: "PROGRAM",
      page: params.page || 0,
      size: params.size || 30,
      ...params,
    };

    const apiParams = { ...updatedParams };
    if ("groupBy" in params) {
      apiParams.groupBy = params.groupBy;
    }

    const response = await api.get(`/api/manual-tasks/filter`, {
      params: apiParams,
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
      },
    });

    if (response.data && Array.isArray(response.data.content)) {
      return response.data.content;
    } else if (response.data && Array.isArray(response.data)) {
      return response.data;
    } else {
      return [];
    }
  } catch (error) {
    return `fetchPriorityOutreach failed: ${error.message}`;
  }
};

export const fetchProviderMember = async (params = {}) => {
  const updatedParams = {
    outreachType: params.outreachType || "",
    filterType: "MEMBER",
    page: params.page || 0,
    size: params.size || 30,
    ...params,
  };

  const apiParams = {
    ...updatedParams,
  };

  const response = await api.get(`/outreach/providers-filter`, {
    params: apiParams,
    headers: {
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
    },
  });

  return response.data.content;
};

export const fetchProviderProvider = async (params = {}) => {
  const updatedParams = {
    outreachType: params.outreachType || "",
    filterType: "PROVIDER",
    page: params.page || 0,
    ...params,
  };

  const apiParams = { ...updatedParams };
  if ("groupBy" in params) {
    apiParams.groupBy = params.groupBy;
  }

  const response = await api.get(`/outreach/providers-filter`, {
    params: apiParams,
    headers: {
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
    },
  });

  return response.data.content;
};

export const fetchProviderPharmacies = async (params = {}) => {
  const updatedParams = {
    outreachType: params.outreachType || "",
    filterType: "PHARMACY",
    page: params.page || 0,
    size: params.size || 30,
    ...params,
  };

  const apiParams = { ...updatedParams };
  if ("groupBy" in params) {
    apiParams.groupBy = params.groupBy;
  }

  const response = await api.get(`/outreach/providers-filter`, {
    params: apiParams,
    headers: {
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
    },
  });

  return response.data.content;
};

export const fetchProviderPrograms = async (params = {}) => {
  const updatedParams = {
    outreachType: params.outreachType || "",
    filterType: "PROGRAM",
    page: params.page || 0,
    size: params.size || 30,
    ...params,
  };

  const apiParams = { ...updatedParams };
  if ("groupBy" in params) {
    apiParams.groupBy = params.groupBy;
  }

  const response = await api.get(`/outreach/providers-filter`, {
    params: apiParams,
    headers: {
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
    },
  });

  return response.data.content;
};

export const fetchPharmacyMember = async (params = {}) => {
  const updatedParams = {
    outreachType: params.outreachType || "",
    filterType: "MEMBER",
    page: params.page || 0,
    size: params.size || 30,
    ...params,
  };

  const apiParams = {
    ...updatedParams,
  };

  const response = await api.get(`/outreach/pharmacies-filter`, {
    params: apiParams,
    headers: {
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
    },
  });

  return response.data.content;
};

export const fetchPharmacyProvider = async (params = {}) => {
  const updatedParams = {
    outreachType: params.outreachType || "",
    filterType: "PROVIDER",
    page: params.page || 0,
    size: params.size || 30,
    ...params,
  };

  const apiParams = { ...updatedParams };
  if ("groupBy" in params) {
    apiParams.groupBy = params.groupBy;
  }

  const response = await api.get(`/outreach/pharmacies-filter`, {
    params: apiParams,
    headers: {
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
    },
  });

  return response.data.content;
};

export const fetchPharmacyPharmacies = async (params = {}) => {
  const updatedParams = {
    outreachType: params.outreachType || "",
    filterType: "PHARMACY",
    page: params.page || 0,
    size: params.size || 30,
    ...params,
  };

  const apiParams = { ...updatedParams };
  if ("groupBy" in params) {
    apiParams.groupBy = params.groupBy;
  }

  const response = await api.get(`/outreach/pharmacies-filter`, {
    params: apiParams,
    headers: {
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
    },
  });

  return response.data.content;
};

export const fetchPharmacyPrograms = async (params = {}) => {
  const updatedParams = {
    outreachType: params.outreachType || "",
    filterType: "PROGRAM",
    page: params.page || 0,
    size: params.size || 30,
    ...params,
  };

  const apiParams = { ...updatedParams };
  if ("groupBy" in params) {
    apiParams.groupBy = params.groupBy;
  }

  const response = await api.get(`/outreach/pharmacies-filter`, {
    params: apiParams,
    headers: {
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
    },
  });

  return response.data.content;
};
export const fetchAssignne = async (params = {}) => {
  const updatedParams = {
    ...params,
  };

  const apiParams = {
    ...updatedParams,
  };

  const response = await api.get(`/api/employees/assignees`, {
    params: apiParams,
    headers: {
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
    },
  });

  return response.data.content;
};

/**
 * Update a member's assignee.
 *
 * @param {number|string} memberId
 * @param {number|string} assigneeId
 * @returns {Promise<any>} server response
 */
export const updateMemberAssignee = async (memberId, assigneeId) => {
  const url = `/outreach/members/${memberId}/assignee`;
  const headers = {
    "Content-Type": "application/json",
    "ngrok-skip-browser-warning": "true",
  };
  const response = await api.patch(url, { assigneeId }, { headers });
  return response.data;
};

/**
 * Bulk update assignee for multiple members.
 *
 * @param {number|null} assigneeId
 * @param {Array<number>} memberIds
 * @returns {Promise<any>} server response
 */
export const updateBulkMemberAssignee = async (assigneeId, memberIds = []) => {
  const url = `/outreach/assignee/bulk`;
  const headers = {
    "Content-Type": "application/json",
    "ngrok-skip-browser-warning": "true",
  };

  const payload = {
    assigneeId,
    memberIds: Array.isArray(memberIds) ? memberIds : [],
  };

  const response = await api.patch(url, payload, { headers });
  return response.data;
};
